import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DigisubservicesService } from '../services/digisubservices.service';
import { forkJoin, of } from 'rxjs';

interface IssueStructure {
  month: string;
  year_suffix: string;
  issues: string[];
  colspan: number;
}

interface RepresentativeSummary {
  jaahirat_karar_baki: number;
  off_rep_name: string;
  data: any[];
  total: number;
  target_value?: number;
  input_value?: number;
  ad_contract_value?: number;
  isSaving?: boolean;   // ✅ shows saving indicator per row
  saveError?: boolean;  // ✅ shows error indicator per row
}

interface CategoryGroup {
  categorywise: string;
  representatives: RepresentativeSummary[];
  total: number;
}

@Component({
  selector: 'app-business-report-briefly',
  templateUrl: './business-report-briefly.component.html',
  styleUrl: './business-report-briefly.component.scss'
})
export class BusinessReportBrieflyComponent {

  businessReportData: any[] = [];
  isLoading: boolean = false;
  headerStructure: IssueStructure[] = [];

  contractRemainingMap: { [repName: string]: number } = {};

  categoryGroups: CategoryGroup[] = [];
  representativeSummaries: RepresentativeSummary[] = [];

  financialYear: string = '';
  financialYearDisplay: string = '';
  financialYearLabel: string = '';

  savedUdhisthaMap: { [repName: string]: number } = {};

  monthMarathi: { [key: string]: string } = {
    'April': 'एप्रिल',
    'May': 'मे',
    'June': 'जून',
    'July': 'जुलै',
    'August': 'ऑगस्ट',
    'September': 'सप्टेंबर',
    'October': 'ऑक्टोबर',
    'November': 'नोव्हेंबर',
    'December': 'डिसेंबर',
    'January': 'जानेवारी',
    'February': 'फेब्रुवारी',
    'March': 'मार्च'
  };

  constructor(
    private dialogRef: MatDialogRef<BusinessReportBrieflyComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private service: DigisubservicesService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {

    // ✅ FIX 1: Set financialYear FIRST, trimmed, before anything else
    this.financialYear = (this.data['financial_year'] ?? '').toString().trim();

    // ✅ FIX 1B: financial_year not passed → derive it from startDate instead
    if (!this.financialYear) {
      this.financialYear = this.getFinancialYearFromDate(this.data['startDate']);
    }

    // ✅ Debug — check in browser console on load
    console.log('=== BusinessReportBrieflyComponent ngOnInit ===');
    console.log('Full dialog data received:', this.data);
    console.log('financialYear resolved to:', JSON.stringify(this.financialYear));

    if (!this.financialYear) {
      console.error(
        '❌ financial_year is EMPTY even after deriving from startDate. Check that startDate is present in dialog data.'
      );
    }

    const payload = {
      start_date:     this.data['startDate'],
      end_date:       this.data['endDate'],
      financial_year: this.financialYear,
      rep_name:       (this.data['repname'] || '').trim(),
      off_rep_name:   (this.data['offRepName'] || '').trim(),
      district:       this.data['district'],
      taluka:         this.data['taluka']
    };

    this.loadBusinessReport(payload);
  }

  loadBusinessReport(payload: any): void {
    this.isLoading = true;

    const businessReport$ = this.service.getBusinessReportBriefly(payload);

    const contractReq = {
      rep_name:     payload.rep_name || '',
      start_date:   payload.start_date,
      end_date:     payload.end_date,
      off_rep_name: payload.off_rep_name || '',
      advt_name:    '',
      fiscal_year:  this.financialYear
    };
    const contractReport$ = this.service.getAllIndetailsContractDetails(contractReq);

    // ✅ FIX 2: Only fetch udhistha if financialYear is non-empty; otherwise use safe fallback
    const udhistha$ = this.financialYear
      ? this.service.getUdhisthaValues(this.financialYear)
      : of({ data: [] });

    forkJoin({
      business: businessReport$,
      contract: contractReport$,
      udhistha: udhistha$
    }).subscribe({
      next: ({ business, contract, udhistha }: any) => {

        if (business) {
          this.businessReportData   = business.business_report || [];
          this.headerStructure      = business.header_structure || [];
          this.financialYearLabel   = business.financial_year_label || '';
          this.financialYearDisplay = business.financial_year_label || '';

          // ── Contract remaining map ──────────────────────────────
          const marchEndPerRep:  { [rep: string]: number } = {};
          const aajVaprlePerRep: { [rep: string]: number } = {};
          const contracts = contract.contracts || [];

          for (const c of contracts) {
            const bookings = c.bookings || [];

            for (const booking of bookings) {
              const repName = (booking.rep_name || '').trim();
              if (!repName) continue;
              marchEndPerRep[repName] =
                (marchEndPerRep[repName] || 0) + (parseFloat(booking.amount) || 0);
            }

            const contractRepName = (
              c.rep_name || (bookings.length > 0 ? bookings[0].rep_name : '') || ''
            ).trim();

            if (contractRepName) {
              aajVaprlePerRep[contractRepName] =
                (aajVaprlePerRep[contractRepName] || 0) + (parseFloat(c.aaj_akher_vaprle) || 0);
            }
          }

          const repToOffRepMap: { [repName: string]: string } = {};
          for (const item of this.businessReportData) {
            const repName    = (item.rep_name    || '').trim();
            const offRepName = (item.off_rep_name || '').trim();
            if (repName && offRepName) repToOffRepMap[repName] = offRepName;
          }

          const offRepRemainingMap: { [offRep: string]: number } = {};
          const allReps = new Set([
            ...Object.keys(marchEndPerRep),
            ...Object.keys(aajVaprlePerRep)
          ]);

          for (const rep of allReps) {
            const remaining = (marchEndPerRep[rep] || 0) - (aajVaprlePerRep[rep] || 0);
            const offRep    = repToOffRepMap[rep] || rep;
            offRepRemainingMap[offRep] =
              (offRepRemainingMap[offRep] || 0) + remaining;
          }

          this.contractRemainingMap = offRepRemainingMap;

          // ✅ FIX 3: Build udhistha map — trim keys, parse floats safely
          this.savedUdhisthaMap = {};
          (udhistha?.data || []).forEach((item: any) => {
            const key = (item.off_rep_name || '').trim();
            if (key) {
              this.savedUdhisthaMap[key] = parseFloat(item.udhistha) || 0;
            }
          });

          console.log('✅ savedUdhisthaMap loaded:', this.savedUdhisthaMap);

          this.processRepresentativeData();

        } else {
          this.businessReportData      = [];
          this.categoryGroups          = [];
          this.representativeSummaries = [];
        }

        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        console.error('❌ Error loading report:', err);
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  processRepresentativeData(): void {
    if (!this.businessReportData || this.businessReportData.length === 0) {
      this.categoryGroups          = [];
      this.representativeSummaries = [];
      return;
    }

    const catMap = new Map<string, Map<string, any[]>>();

    this.businessReportData.forEach(item => {
      const cat     = item.categorywise?.toString() || '1';
      const repName = item.off_rep_name;

      if (!catMap.has(cat)) catMap.set(cat, new Map<string, any[]>());
      const repMap = catMap.get(cat)!;
      if (!repMap.has(repName)) repMap.set(repName, []);
      repMap.get(repName)!.push(item);
    });

    this.categoryGroups = Array.from(catMap.entries()).map(([cat, repMap]) => {

      const representatives: RepresentativeSummary[] = Array.from(repMap.entries())
        .map(([repName, repDataArray]) => {

          let total = 0;
          repDataArray.forEach(item => {
            if (item.issues) {
              Object.values(item.issues).forEach((amount: any) => {
                total += parseFloat(amount) || 0;
              });
            }
          });

          const trimmedRepName      = repName.trim();
          const jaahirat_karar_baki = this.contractRemainingMap[trimmedRepName] || 0;
          // ✅ FIX 4: input_value loaded from DB via savedUdhisthaMap
          const savedUdhistha       = this.savedUdhisthaMap[trimmedRepName] || 0;

          return {
            off_rep_name:        repName,
            data:                repDataArray,
            total,
            target_value:        repDataArray[0]?.target_value || 0,
            input_value:         savedUdhistha,
            ad_contract_value:   0,
            jaahirat_karar_baki,
            isSaving:            false,
            saveError:           false
          };
        })
        .sort((a, b) => {
          const diff = (b.total ?? 0) - (a.total ?? 0);
          return diff !== 0 ? diff : a.off_rep_name.localeCompare(b.off_rep_name, 'mr');
        });

      const catTotal = representatives.reduce((sum, r) => sum + r.total, 0);
      return { categorywise: cat, representatives, total: catTotal };
    });

    this.representativeSummaries = this.categoryGroups.flatMap(cg => cg.representatives);
    console.log('✅ Representatives built:', this.representativeSummaries.length);
  }

  // ── Udhistha input change → auto-save ─────────────────────────────────────
  onInputValueChange(repData: RepresentativeSummary, event: any): void {

    // ✅ Parse value from input
    const inputVal   = parseFloat(event.target.value);
    repData.input_value = isNaN(inputVal) ? 0 : inputVal;

    const offRepName    = (repData.off_rep_name || '').trim();
    const financialYear = this.financialYear.trim();

    console.log('=== onInputValueChange ===');
    console.log('off_rep_name   :', offRepName);
    console.log('financial_year :', financialYear);
    console.log('udhistha       :', repData.input_value);

    if (!offRepName) {
      console.error('❌ off_rep_name is empty — cannot save');
      return;
    }

    if (!financialYear) {
      console.error('❌ financial_year is empty — cannot save. Check dialog data on open.');
      return;
    }

    // ✅ FIX 5: flat payload with exact snake_case keys matching PHP
    const payload = {
      off_rep_name:   offRepName,
      financial_year: financialYear,
      udhistha:       repData.input_value
    };

    console.log('✅ Payload being sent to API:', payload);

    repData.isSaving  = true;
    repData.saveError = false;
    this.cdr.detectChanges();

    this.service.saveUdhistha(payload).subscribe({
      next: (res: any) => {
        console.log('✅ Saved successfully:', res);
        repData.isSaving = false;
        // Keep local map in sync so recalculations stay correct
        this.savedUdhisthaMap[offRepName] = repData.input_value || 0;
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        console.error('❌ Save error:', err);
        repData.isSaving  = false;
        repData.saveError = true;
        this.cdr.detectChanges();
      }
    });
  }

  // ── Input/totals helpers ───────────────────────────────────────────────────

  calculateInputValuesTotal(): number {
    return this.representativeSummaries.reduce((sum, rep) => sum + (rep.input_value || 0), 0);
  }

  getCategoryInputTotal(catGroup: CategoryGroup): number {
    return catGroup.representatives.reduce((sum, rep) => sum + (rep.input_value || 0), 0);
  }

  // ── Colspan helpers ────────────────────────────────────────────────────────

  getTotalColspan(): number {
    return this.headerStructure.reduce((sum, m) => sum + m.colspan, 0);
  }

  getTotalTableCols(): number {
    return 4 + this.getTotalColspan() + 1;
  }

  // ── Value getters ──────────────────────────────────────────────────────────

  getFinancialYearDisplay(): string {
    return this.financialYearLabel;
  }

  getRepMonthIssueValue(
    repData: RepresentativeSummary,
    month: string,
    year_suffix: string,
    issue: string
  ): number {
    if (!repData?.data) return 0;
    const issueKey = `${month}_${year_suffix}_${issue}`;
    let total = 0;
    repData.data.forEach((item: any) => {
      if (item.issues?.[issueKey]) total += parseFloat(item.issues[issueKey]) || 0;
    });
    return total;
  }

  getRepMonthTotal(repData: RepresentativeSummary, month: string, year_suffix: string): number {
    if (!repData?.data) return 0;
    const monthData = this.headerStructure.find(
      m => m.month === month && m.year_suffix === year_suffix
    );
    if (!monthData) return 0;
    return monthData.issues.reduce(
      (sum, issue) => sum + this.getRepMonthIssueValue(repData, month, year_suffix, issue), 0
    );
  }

  // ── Column/grand totals ────────────────────────────────────────────────────

  calculateIssueTotal(month: string, year_suffix: string, issue: string): number {
    return this.representativeSummaries.reduce(
      (sum, rep) => sum + this.getRepMonthIssueValue(rep, month, year_suffix, issue), 0
    );
  }

  calculateCategoryIssueTotal(
    catGroup: CategoryGroup,
    month: string,
    year_suffix: string,
    issue: string
  ): number {
    return catGroup.representatives.reduce(
      (sum, rep) => sum + this.getRepMonthIssueValue(rep, month, year_suffix, issue), 0
    );
  }

  calculateMonthGrandTotal(month: string, year_suffix: string): number {
    const monthData = this.headerStructure.find(
      m => m.month === month && m.year_suffix === year_suffix
    );
    if (!monthData) return 0;
    return monthData.issues.reduce(
      (sum, issue) => sum + this.calculateIssueTotal(month, year_suffix, issue), 0
    );
  }

  calculateGrandTotal(): number {
    return this.representativeSummaries.reduce((sum, rep) => sum + rep.total, 0);
  }

  calculateTargetValuesTotal(): number {
    return this.representativeSummaries.reduce((sum, rep) => sum + (rep.target_value || 0), 0);
  }

  calculateEkunVyavsay(): number {
    return this.representativeSummaries.reduce(
      (sum, rep) => sum + (rep.total || 0) + (rep.jaahirat_karar_baki || 0), 0
    );
  }

  calculateJaahiratKararBakiTotal(): number {
    return this.representativeSummaries.reduce(
      (sum, rep) => sum + (rep.jaahirat_karar_baki || 0), 0
    );
  }

  calculateAdContractValuesTotal(): number {
    return this.representativeSummaries.reduce(
      (sum, rep) => sum + (rep.ad_contract_value || 0), 0
    );
  }

  onAdContractValueChange(repData: RepresentativeSummary, event: any): void {
    repData.ad_contract_value = parseFloat(event.target.value) || 0;
  }

  // ── Target fulfillment ─────────────────────────────────────────────────────

  getTargetFulfillment(repData: RepresentativeSummary): string {
    const total  = repData.total || 0;
    const target = repData.input_value || 0;
    const amtStr = total === 0 ? '' : total.toString();
    if (target === 0) return amtStr;
    const pct = ((total / target) * 100).toFixed(1);
    return amtStr ? `${amtStr} (${pct}%)` : `0 (0%)`;
  }

  getTargetFulfillmentTotal(): string {
    const total  = this.calculateGrandTotal();
    const target = this.calculateInputValuesTotal();
    const amtStr = total === 0 ? '' : total.toString();
    if (target === 0) return amtStr;
    const pct = ((total / target) * 100).toFixed(1);
    return amtStr ? `${amtStr} (${pct}%)` : `0 (0%)`;
  }

  getPct(achieved: number, target: number): string {
    if (!target || target === 0) return '0.0';
    return ((achieved / target) * 100).toFixed(1);
  }

  // ── Misc helpers ───────────────────────────────────────────────────────────

  formatCurrency(value: number | null | undefined): string {
    if (!value || value === 0) return '';
    return value.toString();
  }

  getGlobalRepIndex(catGroupIndex: number, repIndex: number): number {
    let count = 0;
    for (let i = 0; i < catGroupIndex; i++) count += this.categoryGroups[i].representatives.length;
    return count + repIndex + 1;
  }

  monthColors: string[] = [
    '#FFF3E0', '#F3E5F5', '#E8F5E9', '#FFFDE7',
    '#E3F2FD', '#FCE4EC', '#E0F7FA', '#F1F8E9',
    '#FBE9E7', '#E8EAF6', '#F9FBE7', '#E0F2F1'
  ];

  getMonthColor(monthIndex: number): string {
    return this.monthColors[monthIndex % this.monthColors.length];
  }

  // ── Export ─────────────────────────────────────────────────────────────────

  exportToExcel(): void {
    const element = document.getElementById('business-report-table');
    if (element) {
      import('xlsx').then(xlsx => {
        const ws       = xlsx.utils.table_to_sheet(element);
        const wb       = xlsx.utils.book_new();
        xlsx.utils.book_append_sheet(wb, ws, 'व्यवसाय अहवाल');
        const fileName = `व्यवसाय_अहवाल_संक्षिप्त_${new Date().toISOString().split('T')[0]}.xlsx`;
        xlsx.writeFile(wb, fileName);
      });
    }
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  // ✅ NEW: derive financial year string from a date (India FY: April → March)
  private getFinancialYearFromDate(dateStr: string): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';

    const month = d.getMonth() + 1; // 1-12
    const year  = d.getFullYear();

   return month >= 4 ? `${year}-${(year + 1).toString().slice(-2)}` : `${year - 1}-${year.toString().slice(-2)}`;
  }

  // ── Print ──────────────────────────────────────────────────────────────────

  printPageA3Portrait(): void {
    const printSection = document.getElementById('business-report-table');
    if (!printSection) { console.error('Print section not found!'); return; }

    const printWindow = window.open('', '', 'width=1600,height=1000,top=0,left=0,scrollbars=yes');
    if (!printWindow) { console.error('Failed to open print window'); return; }

    const cloned = printSection.cloneNode(true) as HTMLElement;
    cloned.querySelectorAll('input').forEach((inp: any) => {
      const span       = document.createElement('span');
      span.textContent = inp.value || '';
      inp.parentNode?.replaceChild(span, inp);
    });

    printWindow.document.open();
    printWindow.document.write(`
<!DOCTYPE html>
<html>
<head>
  <title>व्यवसाय अहवाल</title>
  <style>
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; box-sizing: border-box; margin: 0; padding: 0; }
    @page { size: A3 landscape; margin: 5mm; }
    html, body { margin: 0; padding: 0; font-family: Arial, sans-serif; background: white; width: 100%; }
    #print-wrapper { width: 100%; transform-origin: top left; display: block; }
    table { border-collapse: collapse; width: 100%; background: white; border: 3px solid #000 !important; table-layout: auto; }
    th, td { border: 1.5px solid #000 !important; padding: 3px 5px !important; font-size: 14px !important; text-align: center; color: #000 !important; white-space: nowrap; }
    thead { display: table-header-group; }
    tfoot { display: table-footer-group; font-weight: bold; }
    thead tr th { border: 1.5px solid #000 !important; font-size: 14px !important; font-weight: bold; padding: 3px 5px !important; }
    tfoot tr td  { border: 1.5px solid #000 !important; font-size: 14px !important; font-weight: bold; padding: 3px 5px !important; }
    .vertical-text,.issue-name-cell,.vertical-header-cell { writing-mode: vertical-rl !important; transform: rotate(180deg) !important; white-space: normal !important; min-height: 100px !important; height: 120px !important; font-size: 14px !important; padding: 4px 2px !important; text-align: left !important; vertical-align: bottom !important; word-break: break-word; overflow: hidden; }
    .title-bg { background-color: #E3F2FD !important; font-size: 14px !important; font-weight: bold; }
    .main-header-bg { background-color: #BBDEFB !important; font-weight: bold; font-size: 14px !important; }
    .month-header { font-weight: bold; font-size: 14px !important; }
    .total-header { background-color: #FFE0B2 !important; font-weight: bold; }
    .input-header { background-color: #FFF9C4 !important; font-weight: bold; }
    .target-header { background-color: #FFE0B2 !important; font-weight: bold; }
    .new-col-header { background-color: #fcfbfa !important; font-weight: bold; }
    .rep-name-header { background-color: #E0E0E0 !important; font-weight: bold; writing-mode: vertical-rl; transform: rotate(180deg); min-width: 20px; }
    .serial-cell { text-align: center; font-weight: bold; }
    .rep-name-cell { text-align: left; font-weight: bold; white-space: nowrap !important; overflow: hidden; text-overflow: ellipsis; max-width: 120px; }
    .amount-cell { text-align: right; font-size: 14px !important; }
    .total-cell { font-weight: bold; text-align: right; }
    .input-cell { text-align: right; }
    .target-cell { text-align: right; font-weight: bold; }
    .new-col-cell { text-align: right; font-weight: bold; }
    .input-total { font-weight: bold; text-align: right; }
    .target-total { font-weight: bold; text-align: right; }
    .grand-total { background-color: #FFE0B2 !important; font-weight: bold; text-align: right; font-size: 14px !important; }
    .footer-total-label { background-color: #E3F2FD !important; font-weight: bold; text-align: center; }
    .total-row td { font-weight: bold; }
    .month-total-bottom-row td { background-color: #BBDEFB !important; font-weight: bold; }
    .month-grand-total { background-color: #BBDEFB !important; font-weight: bold; text-align: center; }
    .category-header-row td { background-color: #EDE7F6 !important; font-weight: bold; text-align: left; }
    .category-subtotal-row td { background-color: #F3E5F5 !important; font-weight: bold; }
    @media print {
      html, body { margin: 0; padding: 0; }
      input { border: none !important; background: transparent !important; }
      th, td { border: 1.5px solid #000 !important; }
      thead tr th { border: 1.5px solid #000 !important; }
      tfoot tr td { border: 1.5px solid #000 !important; }
      .vertical-text,.issue-name-cell,.vertical-header-cell { writing-mode: vertical-rl !important; transform: rotate(180deg) !important; white-space: normal !important; min-height: 100px !important; height: 120px !important; font-size: 14px !important; }
    }
  </style>
</head>
<body>
  <div id="print-wrapper">${cloned.outerHTML}</div>
  <script>
    window.onload = function () {
      var wrapper = document.getElementById('print-wrapper');
      var table = document.querySelector('table');
      if (!table) { setTimeout(function(){ window.print(); window.close(); }, 450); return; }
      var allRows = Array.from(table.querySelectorAll('tr'));
      var occupied = {}, cellMap = {};
      allRows.forEach(function(tr, rowIdx) {
        if (!occupied[rowIdx]) occupied[rowIdx] = {};
        if (!cellMap[rowIdx]) cellMap[rowIdx] = [];
        var colCursor = 0;
        Array.from(tr.querySelectorAll('th, td')).forEach(function(cell) {
          while (occupied[rowIdx][colCursor]) colCursor++;
          var cs = parseInt(cell.getAttribute('colspan') || '1', 10);
          var rs = parseInt(cell.getAttribute('rowspan') || '1', 10);
          cellMap[rowIdx].push({ cell: cell, startCol: colCursor, endCol: colCursor + cs - 1 });
          for (var r = rowIdx; r < rowIdx + rs; r++) {
            if (!occupied[r]) occupied[r] = {};
            for (var c = colCursor; c < colCursor + cs; c++) occupied[r][c] = true;
          }
          colCursor += cs;
        });
      });
      var NORMAL = '1.5px solid #000', MONTH = '3.5px solid #000';
      allRows.forEach(function(_, rowIdx) {
        if (!cellMap[rowIdx]) return;
        cellMap[rowIdx].forEach(function(entry) {
          var cell = entry.cell;
          cell.style.setProperty('border-top', NORMAL, 'important');
          cell.style.setProperty('border-bottom', NORMAL, 'important');
          cell.style.setProperty('border-left', NORMAL, 'important');
          cell.style.setProperty('border-right', NORMAL, 'important');
        });
      });
      var theadRows = Array.from(table.querySelectorAll('thead tr'));
      var monthBoundaries = [], monthRowIdx = -1;
      theadRows.forEach(function(tr, ri) {
        var coloredCount = Array.from(tr.querySelectorAll('th')).filter(function(th) {
          var bg = th.style.backgroundColor || '';
          return bg !== '' && bg !== 'white' && bg !== 'rgb(255, 255, 255)';
        }).length;
        if (coloredCount >= 2 && monthRowIdx === -1) monthRowIdx = ri;
      });
      if (monthRowIdx !== -1 && cellMap[monthRowIdx]) {
        cellMap[monthRowIdx].forEach(function(entry) {
          var bg = entry.cell.style.backgroundColor || '';
          if (bg !== '' && bg !== 'white' && bg !== 'rgb(255, 255, 255)')
            monthBoundaries.push({ startCol: entry.startCol, endCol: entry.endCol });
        });
      }
      var leftCols = new Set(monthBoundaries.map(function(b){ return b.startCol; }));
      var rightCols = new Set(monthBoundaries.map(function(b){ return b.endCol; }));
      allRows.forEach(function(_, rowIdx) {
        if (!cellMap[rowIdx]) return;
        cellMap[rowIdx].forEach(function(entry) {
          if (leftCols.has(entry.startCol)) entry.cell.style.setProperty('border-left', MONTH, 'important');
          if (rightCols.has(entry.endCol)) entry.cell.style.setProperty('border-right', MONTH, 'important');
        });
      });
      function makeVertical(th) {
        th.style.setProperty('writing-mode', 'vertical-rl', 'important');
        th.style.setProperty('transform', 'rotate(180deg)', 'important');
        th.style.setProperty('white-space', 'normal', 'important');
        th.style.setProperty('min-height', '100px', 'important');
        th.style.setProperty('height', '120px', 'important');
        th.style.setProperty('font-size', '14px', 'important');
        th.style.setProperty('padding', '4px 2px', 'important');
        th.style.setProperty('text-align', 'left', 'important');
        th.style.setProperty('vertical-align', 'bottom', 'important');
        th.style.setProperty('overflow', 'hidden', 'important');
        th.style.setProperty('word-break', 'break-word', 'important');
      }
      var issueNameRow = theadRows[theadRows.length - 1];
      if (issueNameRow) Array.from(issueNameRow.querySelectorAll('th')).forEach(makeVertical);
      var verticalKeywords = ['एकूण', 'जाहिरात करार वापरणे बाकी', 'एकूण व्यवसाय'];
      table.querySelectorAll('thead th').forEach(function(th) {
        var text = (th.textContent || '').trim();
        if (verticalKeywords.some(function(kw){ return text.indexOf(kw) !== -1; })) makeVertical(th);
      });
      ['thead', 'tfoot'].forEach(function(section) {
        Array.from(table.querySelectorAll(section + ' tr')).forEach(function(tr) {
          var globalIdx = allRows.indexOf(tr);
          if (globalIdx === -1 || !cellMap[globalIdx]) return;
          cellMap[globalIdx].forEach(function(entry) {
            entry.cell.style.setProperty('border-top', NORMAL, 'important');
            entry.cell.style.setProperty('border-bottom', NORMAL, 'important');
            entry.cell.style.setProperty('border-left', NORMAL, 'important');
            entry.cell.style.setProperty('border-right', NORMAL, 'important');
            if (leftCols.has(entry.startCol)) entry.cell.style.setProperty('border-left', MONTH, 'important');
            if (rightCols.has(entry.endCol)) entry.cell.style.setProperty('border-right', MONTH, 'important');
          });
        });
      });
      table.querySelectorAll('td.rep-name-cell').forEach(function(td) {
        td.style.setProperty('white-space', 'nowrap', 'important');
        td.style.setProperty('overflow', 'hidden', 'important');
        td.style.setProperty('text-overflow', 'ellipsis', 'important');
        td.style.setProperty('max-width', '120px', 'important');
      });
      wrapper.style.transform = 'none';
      wrapper.style.width = '100%';
      void table.offsetWidth;
      var PAGE_WIDTH_PX = 1550;
      var tableNaturalWidth = table.scrollWidth;
      if (tableNaturalWidth > PAGE_WIDTH_PX) {
        var scale = PAGE_WIDTH_PX / tableNaturalWidth;
        wrapper.style.transform = 'scale(' + scale + ')';
        wrapper.style.transformOrigin = 'top left';
        wrapper.style.width = Math.ceil(100 / scale) + '%';
      } else {
        var scale = Math.min(PAGE_WIDTH_PX / tableNaturalWidth, 1.5);
        if (scale > 1.01) {
          wrapper.style.transform = 'scale(' + scale + ')';
          wrapper.style.transformOrigin = 'top left';
          wrapper.style.width = Math.ceil(100 / scale) + '%';
        }
      }
      setTimeout(function () { window.print(); window.close(); }, 600);
    };
  </script>
</body>
</html>`);
    printWindow.document.close();
  }
}